<template>
  <div>
    <ts-page-title
      :title="$t('saleBySerial.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('saleBySerial.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-lg-3">
              <div class="card">
                <div
                  class="card-body border-0 ts-bg-gray-900"
                  style="border-top-left-radius: 8px; border-top-right-radius: 8px;"
                >
                  <div class="ts-text-gray-500">
                    <b>{{ $t('saleBySerial.filterCriteria') }}</b>
                  </div>
                </div>
                <div class="widget-list rounded-bottom">
                  <div class="mb-3 mt-3 ts-px-3">
                    <div>
                      <label class="required">{{
                        $t('saleBySerial.stockOutDate')
                      }}</label>
                      <a-range-picker
                        v-model="model.date_range"
                        class="ts-w-full"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>
                  <div class="mb-3 mt-3 ts-px-3">
                    <div>
                      <label class="required">{{
                        $t('saleBySerial.branch')
                      }}</label>
                      <ts-branch-filter
                        @filter="value => (model.branch_id = value)"
                        :isPlaceholder="true"
                        style="width: 100%"
                      />
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="ts-flex ts-flex-wrap sm:ts-flex-nowrap ts-justify-end ts-gap-2 ts-px-3"
                  >
                    <ts-button
                      outline
                      color="success"
                      @click.prevent="exportExcel"
                      :waiting="exporting"
                      class="ts-w-full sm:ts-w-auto"
                    >
                      <i class="far fa-file-excel" v-if="!exporting"></i>
                      {{ $t('exportExcel') }}
                    </ts-button>

                    <ts-button
                      outline
                      color="primary"
                      @click.prevent="preview"
                      :waiting="waiting"
                      class="ts-w-full sm:ts-w-auto"
                    >
                      <i class="fas fa-file-pdf text-red"></i>
                      {{ $t('preview') }}
                    </ts-button>

                    <ts-button
                      color="primary"
                      @click.prevent="fetchData"
                      :waiting="viewing"
                      class="ts-w-full sm:ts-w-auto"
                    >
                      <i class="far fa-eye" v-if="!viewing"></i>
                      {{ $t('view') }}
                    </ts-button>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-9">
              <div v-if="!showPreview">
                <div class="ts-mb-3 ts-flex ts-justify-end">
                  <ts-input
                    v-model="search"
                    v-tooltip="$t('saleBySerial.search')"
                    :placeholder="$t('search')"
                    style="width: 250px"
                    class="inline-block"
                  />
                </div>
                <div class="overflow-auto" style="max-height: 75vh">
                  <ts-table
                    :columns="columns"
                    :records="resources"
                    :loading="viewing"
                  >
                    <template v-slot="{ record, index }">
                      <td class="text-center">
                        {{
                          (pagination.currentPage - 1) * pagination.perPage +
                            index +
                            1
                        }}
                      </td>
                      <td>{{ record.branch_name_en }}</td>
                      <td>{{ record.barcode }}</td>
                      <td>{{ record.customer_name }}</td>
                      <td>{{ record.invoice_no }}</td>
                      <td>{{ record.pro_group_name_en }}</td>
                      <td>{{ record.product_name_en }}</td>
                      <td>{{ record.serial_no }}</td>
                      <td>{{ record.imei }}</td>
                      <td>{{ record.trade_in_date }}</td>
                      <td>{{ record.stock_out_date }}</td>
                      <td class="text-right">
                        {{ record.cost }}
                      </td>
                      <td class="text-right">
                        {{ record.repair_cost }}
                      </td>
                      <td class="text-right">
                        {{ record.sparepart_cost }}
                      </td>
                      <td class="text-right">
                        {{ record.commission }}
                      </td>
                      <td class="text-right">
                        {{ record.total_cost }}
                      </td>
                      <td class="text-right">
                        {{ record.sale_price }}
                      </td>
                      <td class="text-right">
                        {{ record.profit }}
                      </td>
                      <td class="text-right">
                        {{ record.payment_status }}
                      </td>
                      <td class="text-right">
                        {{ record.payment_amount }}
                      </td>
                      <td class="text-right">
                        {{ record.loan_amount }}
                      </td>
                    </template>
                  </ts-table>
                </div>
                <div class="ts-flex ts-items-center ts-justify-end ts-pt-4">
                  <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                  ></ts-i-pagination>
                </div>
              </div>

              <!-- PDF preview -->
              <div v-else>
                <ts-card>
                  <ts-card-wrapper>
                    <form>
                      <fieldset>
                        <legend>{{ $t('saleBySerial.preview') }}</legend>
                        <ts-preview-pdf v-model="waiting" :src="src" />
                      </fieldset>
                    </form>
                  </ts-card-wrapper>
                </ts-card>
              </div>
            </div>
          </div>
        </ts-loading-banner>
      </ts-panel-wrapper>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { debounce } from 'lodash'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'saleBySerial',
  data() {
    return {
      loading: false,
      exporting: false,
      viewing: false,
      loadingPreData: false,
      waiting: false,
      errors: new Errors(),
      src: null,
      showPreview: false,
      model: {
        date_range: [
          moment()
            .startOf('M')
            .format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ],
        branch_id: []
      }
    }
  },
  computed: {
    ...mapState('report/saleBySerial', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.report.saleBySerial.search
      },
      set(newValue) {
        this.$store.commit('report/saleBySerial/SET_SEARCH', newValue)
        this.$store.commit('report/saleBySerial/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('saleBySerial.no'),
          style: 'text-align:center',
          width: '50px'
        },
        {
          name: this.$t('saleBySerial.branch'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.barCode'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.customerName'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.invoiceNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.group'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.productName'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.serialNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.iMei'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.tradeInDate'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.stockOutDate'),
          style: 'text-align:center'
        },
        {
          name: this.$t('saleBySerial.cost'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.addCost'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.sparepartCost'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.commission'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.totalCost'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.salePrice'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.profit'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.paymentStatus'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.paymentAmount'),
          style: 'text-align:right'
        },
        {
          name: this.$t('saleBySerial.loanAmount'),
          style: 'text-align:right'
        }
      ]
    }
  },
  methods: {
    ...mapActions('report/finance', ['saleBySerial']),
    fetchData(attributes) {
      this.errors = new Errors()
      this.viewing = true
      this.showPreview = false
      this.$store
        .dispatch('report/saleBySerial/fetch', {
          date_range: this.model.date_range,
          branch_id: this.model.branch_id,
          ...attributes
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.viewing = false))
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.showPreview = true
      this.saleBySerial(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/finance/saleBySerialExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    formatCurrencyInteger(value) {
      if (value == null || isNaN(value)) return '0'
      return Math.round(value).toLocaleString() // rounds and adds commas
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'Sale By Serial',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500)
  }
}
</script>
<style scoped>
table th,
table td {
  white-space: nowrap;
}
</style>
